import React from 'react';
import {
  FaJava,
  FaPython,
  FaCuttlefish,
  FaReact,
  FaDatabase,
  FaHtml5,
  FaCss3Alt,
} from 'react-icons/fa';
import {
  bio,
  profile,
  projects,
  journey,
  experience,
  skills,
  socials,
  email,
} from './roomData.js';

const SKILL_ICONS = {
  java: FaJava,
  python: FaPython,
  cpp: FaCuttlefish,
  react: FaReact,
  db: FaDatabase,
  html: FaHtml5,
  css: FaCss3Alt,
};

const statusClass = (s) => `status-${s.toLowerCase().replace(/\s+/g, '-')}`;

// ── REST — About ────────────────────────────────────────────────────────────
const RestRoom = () => (
  <div className="roomGridRest">
    <div className="panel campfire">
      <div className="opPortrait">
        <img src={profile} alt="CJ Stancin" />
      </div>
      <h2 className="opName">CJ STANCIN</h2>
      <p className="opRole">Robotics · Software Engineering · Automation</p>
      <p className="opBio">{bio}</p>
    </div>

    <div className="panel">
      <h3 className="panelTitle">◦ Log entries by the fire</h3>
      <ul className="logList">
        {journey.map((j, i) => (
          <li key={i}>
            <span className="logDot" />
            <div>
              <strong>{j.cardTitle}</strong> — {j.title}
              <span className="logSub">{j.cardDetailedText}</span>
            </div>
          </li>
        ))}
      </ul>
      <h3 className="panelTitle beaconsTitle">◦ Signal beacons</h3>
      <div className="beacons">
        {socials.map((s) => (
          <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="beacon">
            {s.label}
            <span>@{s.handle}</span>
          </a>
        ))}
      </div>
    </div>
  </div>
);

// ── TREASURE — Projects ─────────────────────────────────────────────────────
const TreasureRoom = () => (
  <div className="lootGrid">
    {projects.map((p, i) => (
      <article className="panel relic" key={i}>
        <div className="relicHead">
          <h3>{p.name}</h3>
          {p.status && <span className={`badge ${statusClass(p.status)}`}>{p.status}</span>}
        </div>
        <p className="relicDesc">{p.description}</p>
        <div className="relicMeta">
          <span>◦ {p.contributors}</span>
          <span>↻ {p.updated}</span>
        </div>
        {p.link && (
          <a className="relicLink" href={p.link} target="_blank" rel="noopener noreferrer">
            Open in orbit ↗
          </a>
        )}
      </article>
    ))}
  </div>
);

// ── ELITE — Experience ──────────────────────────────────────────────────────
const EliteRoom = () => (
  <div className="encounterStack">
    {experience.map((e, i) => (
      <article className="panel encounter" key={i}>
        <div className="encounterHead">
          <div>
            <h3>{e.org}</h3>
            <p className="encounterRole">{e.role}</p>
          </div>
          <span className="encounterDates">{e.dates}</span>
        </div>
        <h4 className="subhead">Tactics deployed</h4>
        <ul className="tactics">
          {e.tactics.map((t, j) => (
            <li key={j}>{t}</li>
          ))}
        </ul>
        <h4 className="subhead">Loot acquired</h4>
        <div className="lootChips">
          {e.loot.map((l) => (
            <span key={l} className="chip">
              {l}
            </span>
          ))}
        </div>
      </article>
    ))}
  </div>
);

// ── SHOP — Skills ───────────────────────────────────────────────────────────
const ShopRoom = () => (
  <>
    <p className="shopGreeting">"Mastered wares only, runner. Browse the rack."</p>
    <div className="wares">
      {skills.map((s) => {
        const Icon = SKILL_ICONS[s.icon] || FaReact;
        return (
          <div className="ware" key={s.label}>
            <Icon size={34} />
            <span className="wareName">{s.label}</span>
            <span className="warePrice">MASTERED</span>
          </div>
        );
      })}
    </div>
  </>
);

// ── EVENT — Contact ─────────────────────────────────────────────────────────
const EventRoom = () => (
  <div className="terminal">
    <p className="terminalLead">A terminal hums in the dark. Compose your transmission.</p>
    <form name="contact" method="POST" data-netlify="true" className="transmitForm">
      <input type="hidden" name="form-name" value="contact" />
      <label>
        Name
        <input type="text" name="name" autoComplete="name" />
      </label>
      <label>
        Channel
        <input type="email" name="email" autoComplete="email" />
      </label>
      <label>
        Message
        <textarea name="message" placeholder="Compose your transmission…" />
      </label>
      <button type="submit">Transmit ▸</button>
    </form>
    <div className="beacons terminalBeacons">
      {socials.map((s) => (
        <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="beacon">
          {s.label}
          <span>@{s.handle}</span>
        </a>
      ))}
      <a className="beacon" href={`mailto:${email}`}>
        Email<span>{email}</span>
      </a>
    </div>
  </div>
);

const ROOMS = {
  about: RestRoom,
  projects: TreasureRoom,
  experience: EliteRoom,
  skills: ShopRoom,
  contact: EventRoom,
};

const RoomContent = ({ id }) => {
  const Comp = ROOMS[id];
  return Comp ? <Comp /> : <p className="roomMissing">This room is sealed.</p>;
};

export default RoomContent;
